/**
 * Transport Layer Types
 *
 * Shared types for HTTP and WebSocket transports.
 */

/**
 * Transport interface for HTTP operations
 *
 * Compatible with EntityLoaderPort requirements.
 */
export interface Transport {
  /**
   * GET request
   * @param path - API endpoint path
   * @param params - Query parameters (optional)
   */
  get<T = any>(path: string, params?: Record<string, any>): Promise<T>;

  /**
   * POST request
   * @param path - API endpoint path
   * @param body - Request body (optional)
   */
  post<T = any>(path: string, body?: any): Promise<T>;

  /**
   * PUT request
   */
  put<T = any>(path: string, body?: any): Promise<T>;

  /**
   * DELETE request
   */
  delete<T = any>(path: string): Promise<T>;
}

/**
 * Connection state enum
 *
 * Ported from: connection.rs ConnectionState
 */
export enum ConnectionState {
  Closed = 'closed',
  Connecting = 'connecting',
  Connected = 'connected',
  Ready = 'ready'
}

/**
 * WebSocket message types (after parsing wire format)
 *
 * Ported from: wire_format.rs WebSocketMessage
 */
export type WebSocketMessage =
  | { type: 'open' }
  | { type: 'heartbeat' }
  | { type: 'close' }
  | { type: 'data'; messages: WebSocketMessageData[] };

/**
 * WebSocket message data (contents of `a[...]` frames)
 *
 * Ported from: wire_format.rs WebSocketMessageData
 */
export type WebSocketMessageData =
  | {
      type: 'response';
      requestId: number;
      status: number;
      data: any;
    }
  | {
      type: 'event';
      eventType: string;
      data: any;
    }
  | {
      type: 'md';
      quotes?: any[];
      dom?: any[];
      histograms?: any[];
    }
  | {
      type: 'chart';
      charts: any[];
    }
  | {
      type: 'props';
      repositoryEvent: RepositoryEvent;
    }
  | {
      type: 'sentiment';
      data: any;
    }
  | {
      type: 'shutdown';
      reasonCode: ShutdownReasonCode;
      reason?: string;
    }
  | {
      type: 'disconnect';
    }
  | {
      type: 'unknown';
      eventType?: string;
      data: any;
    };

/**
 * Repository event from WebSocket
 *
 * Entity updates/deletions from server.
 */
export type RepositoryEventType = 'Created' | 'Updated' | 'Deleted';

export interface RepositoryEvent {
  entityType: string;
  eventType: RepositoryEventType;
  id?: number;
  /** payload: entity object or array of entities */
  d?: any | any[];
}

export type ShutdownReasonCode = 'Maintenance' | 'ConnectionQuotaReached' | 'IPQuotaReached'

export interface ShutdownEvent {
  reasonCode: ShutdownReasonCode
  reason?: string
}

/**
 * Transport error types
 */
export class TransportError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    // Optional parsed response payload (e.g., for p-ticket retries)
    public data?: any
  ) {
    super(message);
    this.name = 'TransportError';
  }
}
